// 統一日期輸入 — 包住原生 date input, 只處理「空值時的佔位字」。
//
// 為什麼: Chrome 對空的 date input 依「瀏覽器介面語言」渲染佔位 (中文 Chrome 會
// 出現 yyyy/月/dd 的混合體), 而且 Chrome 無視網頁/元素的 lang 屬性 — 程式碼裡
// 根本沒有那個字串, 也無法用設定改掉 (2026-07-16 user 抓到)。
// 修法: 空值時用 CSS 把原生 segment 文字設透明 (.date-empty), 疊上自訂佔位
// 「yyyy-mm-dd」; 一 focus (要打字) 或有值就恢復原生顯示。 日曆 picker / 鍵盤輸入
// / min-max 驗證全部保留 — 只動「空的長相」。
//
// 支援兩種用法:
//   受控   — 傳 value (絕大多數呼叫點; {...cField()} 這種 spread 也算)
//   非受控 — react-hook-form {...register('x')} (無 value) → 內部以 onChange 追蹤
//            有無值, 並包住 register 的 onChange 一起呼叫。
import {
  type ChangeEvent,
  type InputHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

// forwardRef: react-hook-form 的 {...register('x')} 走 ref 取值 (非受控) —
// 不轉發 ref 的話 RHF 讀不到值、送出會是空的。
export const DateInput = forwardRef<HTMLInputElement, Props>(function DateInput(
  { className = '', value, defaultValue, onChange, ...rest },
  ref,
) {
  const controlled = value !== undefined;
  const [innerHas, setInnerHas] = useState(() => defaultValue != null && defaultValue !== '');
  const innerRef = useRef<HTMLInputElement | null>(null);
  const setRefs = (el: HTMLInputElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };
  // 非受控時每次 render 後對一次 DOM 值 — RHF 的 reset()/setValue() 直接經 ref
  // 塞值、不觸發 onChange, 只靠 onChange 追蹤會把「有值」誤判成空 (edit 預填場景)。
  // 有 guard 不會造成 render loop。
  useEffect(() => {
    if (controlled) return;
    const has = (innerRef.current?.value ?? '') !== '';
    if (has !== innerHas) setInnerHas(has);
  });
  const empty = controlled ? value == null || value === '' : !innerHas;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!controlled) setInnerHas(e.target.value !== '');
    onChange?.(e);
  };

  // input 自帶 w-full 時 wrapper 也要撐滿, 否則 inline-block shrink-wrap 會讓
  // w-full 的 input 塌掉; 反之 (行內緊湊佈局) wrapper 跟著內容寬。
  const full = /\bw-full\b/.test(className);
  return (
    <span className={`date-wrap relative ${full ? 'block w-full' : 'inline-block'}`}>
      <input
        ref={setRefs}
        type="date"
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        className={`${className}${empty ? ' date-empty' : ''}`}
        {...rest}
      />
      {empty && (
        <span
          aria-hidden
          className="date-ph pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-400"
        >
          yyyy-mm-dd
        </span>
      )}
    </span>
  );
});
